import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleModel } from '@/api/model'
import { Label } from '@/components/shadcn/label'
import { Input } from '@/components/shadcn/input'
import { notify } from '@/lib/notify'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { AutoTranslateAllButton } from '@/components/common/translations/AutoTranslateAllButton'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

export function EditInsuranceServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'service-modules'])
  const std = useStandardServiceModuleFields(module)
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const queryClient = useQueryClient()
  const patch = usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleId()
  const passwordPlaceholder = '*****'
  const [invoiceEmail, setInvoiceEmail] = useState<string>('')
  const [pensionApiUserName, setPensionApiUserName] = useState<string>('')
  const [pensionApiPassword, setPensionApiPassword] = useState<string>('')

  useEffect(() => {
    const m = module as any
    setInvoiceEmail(m?.healthInsuranceCompanyInvoiceRecipientEMailAddress ?? '')
    setPensionApiUserName(m?.pensionInsuranceReadApiUserName ?? '')
    setPensionApiPassword(m?.isPensionInsuranceReadApiPasswordSet ? passwordPlaceholder : '')
  }, [module])

  async function handleSave() {
    try {
      let data = {
        ...std.buildBasePayload(),
        healthInsuranceCompanyInvoiceRecipientEMailAddress: invoiceEmail || null,
        pensionInsuranceReadApiUserName: pensionApiUserName || null,
        pensionInsuranceReadApiPassword: ''
      }

      if (pensionApiPassword != null
        && pensionApiPassword.trim() !== ''
        && pensionApiPassword !== passwordPlaceholder) {
        data = {
          ...data,
          pensionInsuranceReadApiPassword: pensionApiPassword
        }
      }

      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: data
      })
      await std.saveTranslations()
      std.triggerTranslation(String(module.id), autoTranslateAll)
      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
      })
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="invoice-email">{t('insurance.edit.invoiceEmail')}</Label>
      <Input
        id="invoice-email"
        type="email"
        value={invoiceEmail}
        onChange={(e) => setInvoiceEmail(e.target.value)}
        placeholder={t('insurance.edit.invoiceEmailPlaceholder')}
      />
      <Label htmlFor="pension-api-user-name">{t('insurance.edit.pensionApiUserName')}</Label>
      <Input
        id="pension-api-user-name"
        type="text"
        value={pensionApiUserName}
        onChange={(e) => setPensionApiUserName(e.target.value)}
      />
      <Label htmlFor="pension-api-password">{t('insurance.edit.pensionApiPassword')}</Label>
      <Label>{t('insurance.edit.pensionApiPasswordDisclaimer')}</Label>
      <Input
        id="pension-api-password"
        type="password"
        value={pensionApiPassword}
        onChange={(e) => setPensionApiPassword(e.target.value)}
      />
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('insurance.edit.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={std} moduleType={module.type} autoTranslateAll={autoTranslateAll}/>}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending}
      autoTranslateButton={
        <AutoTranslateAllButton
          properties={std.autoTranslateProperties}
          autoTranslateAll={autoTranslateAll}
          onAutoTranslateAllChange={setAutoTranslateAll}
        />
      }
    />
  )
}
